import { Calendar, Eye, EyeOff, FileText, Hash, Link, Image as LucideImage } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { FramerObjectDisplay } from './FramerObjectDisplay'

interface FramerCMSItem {
  id?: string
  slug?: string
  draft?: boolean
  fieldData?: Record<
    string,
    {
      type?: string
      value?: any
    }
  >
  [key: string]: any
}

interface FramerCMSItemDisplayProps {
  items: FramerCMSItem[]
  title?: string
  showFullData?: boolean
  className?: string
}

/**
 * Specialized component for displaying Framer CMS items with structured field data
 */
export function FramerCMSItemDisplay({
  items,
  title = 'CMS Items',
  showFullData = false,
  className = '',
}: FramerCMSItemDisplayProps) {
  if (!items || items.length === 0) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-600">
          <FileText className="h-4 w-4" />
          <span className="text-sm font-medium">No items found</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-purple-600" />
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => (
          <CMSItemCard
            key={item.id || item.slug || index}
            item={item}
            showFullData={showFullData}
          />
        ))}
      </div>
    </div>
  )
}

interface CMSItemCardProps {
  item: FramerCMSItem
  showFullData: boolean
}

function CMSItemCard({ item, showFullData }: CMSItemCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Extract common fields from fieldData
  const title = extractFieldValue(item.fieldData, ['title', 'name', 'heading'])
  const content = extractFieldValue(item.fieldData, ['content', 'body', 'description', 'text'])
  const imageUrl = extractFieldValue(item.fieldData, ['image', 'thumbnail', 'hero', 'picture'])

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Item Header */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50">
          <FileText className="h-5 w-5 text-purple-600" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-base font-medium text-gray-900">
              {title || item.slug || 'Untitled Item'}
            </h4>
            {item.draft && (
              <span className="flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">
                <EyeOff className="h-3 w-3" />
                Draft
              </span>
            )}
            {!item.draft && (
              <span className="flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">
                <Eye className="h-3 w-3" />
                Published
              </span>
            )}
          </div>

          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
            {item.id && (
              <span className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                {item.id}
              </span>
            )}
            {item.slug && (
              <span className="flex items-center gap-1">
                <Link className="h-3 w-3" />
                {item.slug}
              </span>
            )}
            {item.fieldData && <span>{Object.keys(item.fieldData).length} fields</span>}
          </div>

          {/* Preview content */}
          {content && (
            <div className="mt-2">
              <p className="line-clamp-2 text-sm text-gray-600">
                {typeof content === 'string'
                  ? content.replace(/<[^>]*>/g, '').substring(0, 150) +
                    (content.length > 150 ? '...' : '')
                  : JSON.stringify(content).substring(0, 150) + '...'}
              </p>
            </div>
          )}

          {/* Image preview */}
          {imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('http') && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <LucideImage className="h-3 w-3" />
                <span className="truncate">{imageUrl}</span>
              </div>
            </div>
          )}
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            ▶
          </span>
        </button>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="space-y-3 border-t border-gray-200 p-4">
          {/* Field Data */}
          {item.fieldData && Object.keys(item.fieldData).length > 0 && (
            <div>
              <h5 className="mb-2 text-sm font-medium text-gray-700">Field Data</h5>
              <div className="space-y-2">
                {Object.entries(item.fieldData).map(([fieldId, fieldInfo]) => (
                  <FieldDataItem key={fieldId} fieldId={fieldId} fieldInfo={fieldInfo} />
                ))}
              </div>
            </div>
          )}

          {/* Full Item Data */}
          {showFullData && (
            <FramerObjectDisplay
              title="Complete Item Data"
              data={item}
              defaultExpanded={false}
              icon={<FileText className="h-4 w-4 text-purple-600" />}
            />
          )}
        </div>
      )}
    </div>
  )
}

interface FieldDataItemProps {
  fieldId: string
  fieldInfo: any
}

function FieldDataItem({ fieldId, fieldInfo }: FieldDataItemProps) {
  const type = fieldInfo?.type
  const value = fieldInfo?.value

  const getFieldIcon = (fieldType?: string): ReactNode => {
    switch (fieldType?.toLowerCase()) {
      case 'image':
        return <LucideImage className="h-3 w-3" />
      case 'date':
        return <Calendar className="h-3 w-3" />
      case 'link':
        return <Link className="h-3 w-3" />
      default:
        return <FileText className="h-3 w-3" />
    }
  }

  const getTypeColor = (fieldType?: string) => {
    switch (fieldType?.toLowerCase()) {
      case 'string':
      case 'formattedtext':
        return 'bg-green-100 text-green-800'
      case 'number':
        return 'bg-blue-100 text-blue-800'
      case 'boolean':
        return 'bg-purple-100 text-purple-800'
      case 'date':
        return 'bg-orange-100 text-orange-800'
      case 'image':
      case 'file':
        return 'bg-pink-100 text-pink-800'
      case 'link':
        return 'bg-indigo-100 text-indigo-800'
      case 'multiCollectionReference':
      case 'collectionReference':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatValue = (val: any, fieldType?: string): string => {
    if (val === null || val === undefined) return 'null'

    if (fieldType === 'formattedText' && typeof val === 'string') {
      // Strip HTML tags for preview
      return val.replace(/<[^>]*>/g, '').substring(0, 100) + (val.length > 100 ? '...' : '')
    }

    if (typeof val === 'string' && val.length > 100) {
      return val.substring(0, 100) + '...'
    }

    if (Array.isArray(val)) {
      return `[${val.length} items]`
    }

    if (typeof val === 'object') {
      return JSON.stringify(val, null, 2).substring(0, 100) + '...'
    }

    return String(val)
  }

  return (
    <div className="flex items-start justify-between rounded border border-gray-100 bg-gray-50 p-3">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <div className="flex flex-shrink-0 items-center gap-1">
          {getFieldIcon(type)}
          <span className="font-mono text-xs text-gray-500">{fieldId}</span>
        </div>

        {type && (
          <span
            className={`flex-shrink-0 rounded px-2 py-0.5 text-xs font-medium ${getTypeColor(type)}`}
          >
            {type}
          </span>
        )}
      </div>

      <div className="ml-2 min-w-0 flex-1">
        <div className="break-words text-sm text-gray-900">{formatValue(value, type)}</div>
      </div>
    </div>
  )
}

/**
 * Helper function to extract common field values from fieldData
 */
function extractFieldValue(fieldData: Record<string, any> | undefined, fieldNames: string[]): any {
  if (!fieldData) return undefined

  for (const name of fieldNames) {
    // Try direct field name match
    if (fieldData[name]?.value !== undefined) {
      return fieldData[name].value
    }

    // Try field names that might contain the target name
    const matchingKey = Object.keys(fieldData).find((key) =>
      key.toLowerCase().includes(name.toLowerCase())
    )

    if (matchingKey && fieldData[matchingKey]?.value !== undefined) {
      return fieldData[matchingKey].value
    }
  }

  return undefined
}
